import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Plus, RefreshCw } from 'lucide-react';
import type {
  ArtLibraryItem,
  ArtLibrarySnapshot,
  ArtModelListResult,
  ArtModelRecord,
} from '@volley/crucible-ipc-types';
import { AddAssetDialog, CATEGORIES } from './AddAssetDialog';
import { AddModelDialog } from './AddModelDialog';
import { ArtAssetInspector } from './ArtAssetInspector';
import { useCrucibleClient } from '../../transport/context.js';

const ModelAssetInspector = lazy(async () => {
  const module = await import('./ModelAssetInspector.js');
  return { default: module.ModelAssetInspector };
});

/**
 * The Art tab's Library mode (crucible#2611 Task 5) — a category-chip grid
 * over every asset/animation/audio entry the game's forge manifest knows
 * about, with a selected item opening the `ArtAssetInspector` for the
 * revision loop (takes strip, feedback → redo, approve → export).
 *
 * Unlike `AssetReviewPanel`'s continuous 1.5s poll (a build-time review is
 * actively GENERATING, so live status matters every tick), the Library
 * fetches `art:library` once on mount and explicitly REFETCHES after any
 * mutating action completes (redo, swap-take, approve+export) — the full
 * projection resolves every take of every asset to a base64 data URL, a
 * meaningfully heavier payload than the build-time snapshot's "current take
 * only" shape, and nothing here changes without a user action driving it.
 *
 * crucible#2868 AC-2: this is the MAINTENANCE surface for a game that
 * already went through art generation once — add a new asset to a category
 * (AC-3's per-category tiles), tweak or redo one that's already there. It is
 * no longer the from-scratch entry point for a fresh game (that's
 * `ArtWorkspace`'s guided flow, gated on whether the style/moodboard anchor
 * is locked); the copy below is written for "this game's art already
 * exists," never "start here."
 */

/** The special chip ids for the two groupings that live outside `items`'
 *  per-entry `category` field — `ArtLibrarySnapshot` keeps them as separate
 *  arrays exactly as the engine manifest does. */
const ANIMATIONS_CHIP = 'animations' as const;
const AUDIO_CHIP = 'audio' as const;
const MODELS_CHIP = 'models' as const;
const ALL_CHIP = 'all' as const;
type ChipId =
  | typeof ALL_CHIP
  | typeof ANIMATIONS_CHIP
  | typeof AUDIO_CHIP
  | typeof MODELS_CHIP
  | string;
type ModelListItem = ArtModelListResult['models'][number];

/**
 * Which chip's filter would actually SHOW the asset `id`, or null if the
 * snapshot doesn't (yet) contain it in any bucket. `ArtLibrarySnapshot` is
 * multi-bucket: `items` (every NON-audio asset — shown under the "All" chip)
 * plus `audio` and `animations`, two separate top-level groupings each with
 * their OWN chip (see the chip-constant docblock and `buildLibrarySnapshot`,
 * which splits audio out by `category === 'audio'`). The add flow and the
 * pending-resolve effect both route to this chip so a just-added asset is
 * visible + selected regardless of its category.
 *
 * crucible#2707 (Bugbot, commit dd4151d): the add flow scanned only
 * `snapshot.items` (directly, and via `visibleItems`, which on the reset
 * "All" chip IS `items`). An AUDIO add lands in `snapshot.audio`, so a
 * SUCCESSFUL confirming refresh was mis-read as failed/raced — the "added,
 * will appear" notice stuck forever and the asset never auto-selected.
 * "All" stays items-only by design; the fix targets the RIGHT chip.
 */
function chipForAddedAsset(snapshot: ArtLibrarySnapshot | null, id: string): ChipId | null {
  if (!snapshot) return null;
  if (snapshot.items.some((item) => item.id === id)) return ALL_CHIP;
  if (snapshot.audio.some((item) => item.id === id)) return AUDIO_CHIP;
  if (snapshot.animations.some((item) => item.id === id)) return ANIMATIONS_CHIP;
  return null;
}

/**
 * The addable-asset category a given chip pre-scopes to (crucible#2868
 * AC-3), or null if the chip doesn't map to exactly one. `ALL_CHIP` mixes
 * every category, so there's nothing single to pre-scope to. `ANIMATIONS_CHIP`
 * has no addable equivalent today — `art:draft-spec`'s category schema
 * (`draftSpecCategorySchema`, ipc-validators.ts) has no `'animation'` entry,
 * so this deliberately does NOT offer a per-category add tile there rather
 * than open a dialog whose draft would just come back `{ok:false}`.
 */
function addableCategoryForChip(chip: ChipId): string | null {
  if (chip === AUDIO_CHIP) return 'audio';
  if (chip === ALL_CHIP || chip === ANIMATIONS_CHIP) return null;
  return (CATEGORIES as readonly string[]).includes(chip) ? chip : null;
}

export interface ArtLibraryProps {
  /** kebab-case game id whose generated art this Library browses. */
  gameId: string;
}

export function ArtLibrary({ gameId }: ArtLibraryProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [snapshot, setSnapshot] = useState<ArtLibrarySnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeChip, setActiveChip] = useState<ChipId>(ALL_CHIP);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [models, setModels] = useState<ModelListItem[] | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [showAddModelDialog, setShowAddModelDialog] = useState(false);
  const modelListRequest = useRef({ gameId, sequence: 0 });
  if (modelListRequest.current.gameId !== gameId) {
    modelListRequest.current = {
      gameId,
      sequence: modelListRequest.current.sequence + 1,
    };
  }
  /** Which category (if any) the currently-open add dialog should pre-scope
   *  to (crucible#2868 AC-3) — set when opened via a per-category "+ Add"
   *  tile, left `undefined` for the chip-bar's unscoped entry point. */
  const [addDialogCategory, setAddDialogCategory] = useState<string | undefined>(undefined);
  /** Set when `art:add-asset` succeeded but the confirming refresh either
   *  failed or raced ahead of the write, so the new id isn't in `snapshot`
   *  yet (crucible#2611 Bugbot round 7, finding E: "Add success hides on
   *  refresh fail"). Drives a notice distinct from a generic stale-refresh
   *  banner, and self-resolves the moment a later refresh (this one retried,
   *  or any other — e.g. the inspector's `onChanged`) makes the id visible. */
  const [pendingAddedId, setPendingAddedId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<ArtLibrarySnapshot | null> => {
    try {
      const snap = await client.invoke('art:library', { gameId });
      setSnapshot(snap);
      setError(null);
      return snap;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [client, gameId]);

  const refreshModels = useCallback(async (): Promise<ModelListItem[] | null> => {
    const requestGameId = gameId;
    const sequence = ++modelListRequest.current.sequence;
    const isCurrent = (): boolean =>
      modelListRequest.current.gameId === requestGameId &&
      modelListRequest.current.sequence === sequence;
    setModelsLoading(true);
    setModelError(null);
    try {
      const result = await client.invoke('art:model:list', { gameId });
      if (!isCurrent()) return null;
      setModels(result.models);
      return result.models;
    } catch (err) {
      if (!isCurrent()) return null;
      setModelError(err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      if (isCurrent()) setModelsLoading(false);
    }
  }, [client, gameId]);

  /**
   * crucible#2611 Task 6 — after a successful `art:add-asset`, refetch FIRST
   * (so the new item is actually IN the snapshot) before selecting it, then
   * route to the chip that will SHOW it — selecting before the refresh lands
   * would be immediately undone by the selection-invariant effect below (the
   * new id isn't in the stale `visibleItems` yet).
   *
   * crucible#2707 (Bugbot, commit dd4151d) — the snapshot is multi-bucket, so
   * "reset to All" only reveals a NON-audio asset: "All" is `items`-only by
   * design (audio/animations are separate top-level chips). An audio add lands
   * in `snapshot.audio`, so the old `snap?.items.some(...)` check treated a
   * SUCCESSFUL refresh as failed and the asset never showed. Route to the
   * bucket-appropriate chip (`chipForAddedAsset`) instead, so any category is
   * visible + selected.
   *
   * crucible#2611 Bugbot round 7 (finding E): the add itself already
   * succeeded — this handler only fires on `{ok:true}` from AddAssetDialog.
   * If the CONFIRMING refresh fails or races ahead of the write, the new id
   * isn't in ANY bucket yet; track it as `pendingAddedId` so a distinct
   * notice can say the add worked (never "the add failed"), and the effect
   * below resolves it — routing to the right chip — once the data catches up.
   */
  const onAssetAdded = useCallback(
    async (newId: string): Promise<void> => {
      setShowAddDialog(false);
      setAddDialogCategory(undefined);
      setActiveChip(ALL_CHIP);
      const snap = await refresh();
      const chip = chipForAddedAsset(snap, newId);
      if (chip) {
        setActiveChip(chip);
        setSelectedId(newId);
      } else {
        setPendingAddedId(newId);
      }
    },
    [refresh],
  );

  const onModelAdded = useCallback(
    async (record: ArtModelRecord): Promise<void> => {
      setShowAddModelDialog(false);
      const refreshedModels = await refreshModels();
      if (refreshedModels?.some((modelItem) => modelItem.record.id === record.id)) {
        setSelectedModelId(record.id);
      }
    },
    [refreshModels],
  );

  /** Opens the add dialog, optionally pre-scoped to `category` (crucible#2868
   *  AC-3) — the chip-bar's unscoped entry point calls this with no argument,
   *  a per-category "+ Add" tile calls it with `addableCategoryForChip`'s
   *  result for the chip it's rendered inside. */
  const openAddDialog = useCallback((category?: string) => {
    setAddDialogCategory(category);
    setShowAddDialog(true);
  }, []);

  useEffect(() => {
    setSnapshot(null);
    setError(null);
    setSelectedId(null);
    setActiveChip(ALL_CHIP);
    setPendingAddedId(null);
    setAddDialogCategory(undefined);
    setModels(null);
    setModelsLoading(false);
    setModelError(null);
    setSelectedModelId(null);
    setShowAddModelDialog(false);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (activeChip === MODELS_CHIP && models === null && !modelsLoading) {
      void refreshModels();
    }
  }, [activeChip, models, modelsLoading, refreshModels]);

  const categories = useMemo(() => {
    if (!snapshot) return [];
    const fromItems = Array.from(new Set(snapshot.items.map((i) => i.category))).sort();
    const chips: ChipId[] = [ALL_CHIP, ...fromItems];
    if (snapshot.animations.length > 0) chips.push(ANIMATIONS_CHIP);
    if (snapshot.audio.length > 0) chips.push(AUDIO_CHIP);
    chips.push(MODELS_CHIP);
    return chips;
  }, [snapshot]);

  const visibleItems = useMemo((): ArtLibraryItem[] => {
    if (!snapshot) return [];
    if (activeChip === ALL_CHIP) return snapshot.items;
    if (activeChip === ANIMATIONS_CHIP) return snapshot.animations;
    if (activeChip === AUDIO_CHIP) return snapshot.audio;
    if (activeChip === MODELS_CHIP) return [];
    return snapshot.items.filter((i) => i.category === activeChip);
  }, [snapshot, activeChip]);

  // crucible#2868 AC-3: the active chip's addable category, if it has one —
  // drives the per-category "+ Add" tile inside the grid section below.
  const addCategory = addableCategoryForChip(activeChip);

  // Invariant (crucible#2611 Bugbot MEDIUM round 4): selectedId is always
  // either null or present in visibleItems. Before this, switching the
  // active chip left a stale selectedId pointing at an item the NEW filter
  // excludes — the grid showed no selection while the inspector kept
  // editing an item that wasn't even visible. Deriving off `visibleItems`
  // (rather than only handling it in the chip's onClick) covers BOTH
  // triggers that can violate the invariant: a manual chip click, AND a
  // refresh (post redo/swap-take/approve) whose fresh snapshot moves the
  // selected item to a different chip's bucket while `activeChip` stays put
  // — `selectedItem` below searches ALL collections regardless of chip, so
  // without this the item would still resolve and the inspector would keep
  // showing it even though the grid no longer does.
  useEffect(() => {
    if (selectedId && !visibleItems.some((i) => i.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, visibleItems]);

  // Resolve a pending add (crucible#2611 Bugbot round 7, finding E) the
  // moment a LATER refresh — this one retried, or any other, e.g. the
  // inspector's `onChanged` after an unrelated action — brings the
  // successfully-added asset into the snapshot. Routes to the chip that
  // shows its bucket, selects it, and clears the notice.
  //
  // crucible#2707: scans `snapshot` across ALL buckets, not `visibleItems`
  // (which on the "All" chip is `items`-only) — otherwise an audio add's
  // pending notice never cleared, since audio never appears under "All".
  useEffect(() => {
    if (!pendingAddedId) return;
    const chip = chipForAddedAsset(snapshot, pendingAddedId);
    if (chip) {
      setActiveChip(chip);
      setSelectedId(pendingAddedId);
      setPendingAddedId(null);
    }
  }, [pendingAddedId, snapshot]);

  const selectedItem = useMemo(
    () =>
      selectedId
        ? ([
            ...(snapshot?.items ?? []),
            ...(snapshot?.animations ?? []),
            ...(snapshot?.audio ?? []),
          ].find((i) => i.id === selectedId) ?? null)
        : null,
    [snapshot, selectedId],
  );
  const selectedModel = useMemo(
    () => models?.find((modelItem) => modelItem.record.id === selectedModelId) ?? null,
    [models, selectedModelId],
  );

  useEffect(() => {
    if (selectedModelId && models && !selectedModel) setSelectedModelId(null);
  }, [models, selectedModel, selectedModelId]);

  // A failed INITIAL load has nothing to fall back on — full-screen error,
  // no grid to retain. A failed REFRESH after the initial load succeeded
  // (post redo/swap-take/approve, crucible#2611 Bugbot MEDIUM round 3) is
  // handled separately below, as a non-blocking notice over the retained
  // grid — see that comment for why blanking the screen here would be wrong.
  if (error && !snapshot) {
    return (
      <div
        role="alert"
        className="flex h-full items-center justify-center bg-crucible-bg px-6"
        data-testid="art-library-error"
      >
        <div className="w-full max-w-xl rounded-lg border border-red-500/30 bg-red-500/5 p-4 shadow-2xl">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div
        className="flex h-full items-center justify-center gap-3 text-sm text-white/60"
        data-testid="art-library-loading"
      >
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
        Loading your art library…
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col gap-4 overflow-hidden md:flex-row"
      data-testid="art-library"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-auto pr-1">
        {/*
         * A refresh AFTER the initial load (post redo/swap-take/approve) can
         * fail while a good snapshot is still on screen (crucible#2611
         * Bugbot MEDIUM round 3) — before this, `error` was set but never
         * rendered once `snapshot` existed, so the user kept looking at
         * stale takes/status with zero signal anything went wrong.
         * Non-blocking: the notice sits above the retained grid rather than
         * replacing it (the stale data is still the best data we have), and
         * clears itself the moment `refresh()` next succeeds (it
         * unconditionally `setError(null)`s on its success path).
         */}
        {error && (
          <div
            role="alert"
            data-testid="art-library-refresh-error"
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
          >
            Couldn't refresh — showing the last loaded state.
          </div>
        )}

        {/*
         * crucible#2611 Bugbot round 7, finding E: distinct from the banner
         * above — this fires only after a SUCCESSFUL art:add-asset whose
         * confirming refresh didn't (yet) show the new item, so it must never
         * read as "the add failed". Self-clears via the effect above once a
         * later refresh makes the item visible.
         */}
        {pendingAddedId && (
          <div
            role="status"
            data-testid="art-library-add-pending"
            className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/10 px-3 py-2 text-xs text-crucible-accent"
          >
            Added "{pendingAddedId}" — it will appear once the Library refreshes.
          </div>
        )}

        {/*
         * crucible#2868 AC-2: an explicit maintenance-framing line — this
         * page is for a game whose art already exists, not the place to
         * start a new one from scratch (that's the guided flow ArtWorkspace
         * branches to when the style/moodboard anchor isn't locked yet).
         */}
        <p className="text-xs text-white/40" data-testid="art-library-subtitle">
          Add, tweak, or redo art for this game.
        </p>

        {/* Category chips */}
        <div className="flex flex-wrap items-center gap-2" data-testid="art-library-chips">
          {categories.map((chip) => (
            <button
              key={chip}
              type="button"
              data-testid={`art-library-chip-${chip}`}
              data-active={activeChip === chip}
              onClick={() => setActiveChip(chip)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                activeChip === chip
                  ? 'bg-crucible-accent text-black'
                  : 'bg-white/10 text-white/70 hover:bg-white/15'
              }`}
            >
              {chip === ALL_CHIP ? 'All' : chip}
            </button>
          ))}
          {activeChip === MODELS_CHIP ? (
            <AddModelDialog
              gameId={gameId}
              open={showAddModelDialog}
              onOpenChange={setShowAddModelDialog}
              onAdded={(record) => void onModelAdded(record)}
              trigger={
                <button
                  type="button"
                  data-testid="art-library-add-model"
                  className="flex items-center gap-1 rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-white/60 transition hover:bg-white/10 hover:text-white/85"
                >
                  <Plus aria-hidden="true" className="h-3 w-3" />
                  Add model
                </button>
              }
            />
          ) : (
            <button
              type="button"
              onClick={() => openAddDialog()}
              data-testid="art-library-add-asset"
              className="rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-white/50 transition hover:bg-white/10 hover:text-white/80"
            >
              + Add asset
            </button>
          )}
        </div>

        {activeChip === MODELS_CHIP ? (
          <ModelCollection
            models={models}
            loading={modelsLoading}
            error={modelError}
            selectedId={selectedModelId}
            onSelect={setSelectedModelId}
            onRetry={refreshModels}
          />
        ) : visibleItems.length === 0 && !addCategory ? (
          <div
            className="flex flex-1 items-center justify-center text-sm text-white/40"
            data-testid="art-library-empty"
          >
            Nothing in this category yet — use "+ Add asset" to add one.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {addCategory && (
              <button
                type="button"
                onClick={() => openAddDialog(addCategory)}
                data-testid="art-library-add-asset-category"
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-white/20 text-xs font-medium text-white/50 transition hover:border-white/40 hover:text-white/80"
              >
                <span className="text-lg leading-none">+</span>
                Add {addCategory}
              </button>
            )}
            {visibleItems.map((item) => (
              <LibraryThumb
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                onSelect={() => setSelectedId(item.id)}
              />
            ))}
          </div>
        )}

        {/*
         * crucible#2611's original copy ("No assets in this category yet.")
         * read like a from-scratch empty state. crucible#2868 AC-2: reframe
         * as maintenance — nudge toward the "+ Add asset" action rather than
         * implying the category (or the game) hasn't started.
         */}
      </div>

      {/* Inspector pane */}
      <div
        className="h-[min(52vh,520px)] w-full shrink-0 overflow-auto border-t border-white/10 pt-4 md:h-auto md:w-[360px] md:border-l md:border-t-0 md:pl-4 md:pt-0"
        data-testid="art-library-inspector-scrollport"
      >
        {activeChip === MODELS_CHIP && selectedModel ? (
          <Suspense
            fallback={
              <div
                className="flex min-h-40 items-center justify-center gap-2 text-xs text-white/45"
                data-testid="model-inspector-module-loading"
                role="status"
                aria-live="polite"
              >
                <RefreshCw aria-hidden="true" className="h-4 w-4 animate-spin" />
                Loading model inspector…
              </div>
            }
          >
            <ModelAssetInspector
              key={selectedModel.record.id}
              gameId={gameId}
              model={selectedModel}
              onChanged={refreshModels}
            />
          </Suspense>
        ) : activeChip === MODELS_CHIP ? (
          <div
            className="flex h-full items-center justify-center text-center text-sm text-white/40"
            data-testid="art-library-no-model-selection"
          >
            Select a model to review its pipeline and versions.
          </div>
        ) : selectedItem ? (
          <ArtAssetInspector
            key={selectedItem.id}
            gameId={gameId}
            item={selectedItem}
            onChanged={refresh}
          />
        ) : (
          <div
            className="flex h-full items-center justify-center text-center text-sm text-white/40"
            data-testid="art-library-no-selection"
          >
            Select an asset to review its takes, ask for a tweak or redo, or approve it.
          </div>
        )}
      </div>

      {showAddDialog && (
        <AddAssetDialog
          gameId={gameId}
          initialCategory={addDialogCategory}
          onClose={() => {
            setShowAddDialog(false);
            setAddDialogCategory(undefined);
          }}
          onAdded={(newId) => void onAssetAdded(newId)}
        />
      )}
    </div>
  );
}

function ModelCollection({
  models,
  loading,
  error,
  selectedId,
  onSelect,
  onRetry,
}: {
  models: ModelListItem[] | null;
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRetry: () => Promise<ModelListItem[] | null>;
}): React.JSX.Element {
  if (loading && models === null) {
    return (
      <div
        className="flex flex-1 items-center justify-center gap-2 text-sm text-white/45"
        data-testid="art-library-model-loading"
      >
        <RefreshCw aria-hidden="true" className="h-4 w-4 animate-spin" />
        Loading models…
      </div>
    );
  }
  if (error && models === null) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
        data-testid="art-library-model-error"
        role="alert"
      >
        <p className="max-w-sm text-sm text-red-300">{error}</p>
        <button
          type="button"
          onClick={() => void onRetry()}
          className="flex h-10 items-center gap-1.5 rounded-md bg-white/10 px-3 text-xs font-medium text-white/80 hover:bg-white/15"
        >
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }
  if (!models || models.length === 0) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
        data-testid="art-library-model-empty"
      >
        <Box aria-hidden="true" className="h-8 w-8 text-white/25" />
        <p className="text-sm text-white/45">No 3D models yet.</p>
      </div>
    );
  }
  return (
    <>
      {error && (
        <div
          role="alert"
          data-testid="art-library-model-refresh-error"
          className="flex items-center justify-between gap-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
        >
          <span>Couldn&apos;t refresh models. Showing the last loaded state.</span>
          <button
            type="button"
            onClick={() => void onRetry()}
            className="shrink-0 font-medium text-white/75 hover:text-white"
          >
            Retry
          </button>
        </div>
      )}
      <div
        className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3"
        data-testid="art-library-models"
      >
        {models.map((modelItem) => {
          const recipe =
            typeof modelItem.record.spec.recipe === 'string'
              ? modelItem.record.spec.recipe
              : 'model';
          const status = modelItem.record.approvedVersionId
            ? 'approved'
            : modelItem.record.currentVersionId
              ? 'generated'
              : 'not generated';
          return (
            <button
              key={modelItem.record.id}
              type="button"
              onClick={() => onSelect(modelItem.record.id)}
              data-testid={`art-library-model-${modelItem.record.id}`}
              data-selected={selectedId === modelItem.record.id}
              className={`flex min-h-36 min-w-0 flex-col justify-between rounded-lg border p-3 text-left transition ${selectedId === modelItem.record.id ? 'border-crucible-accent bg-white/10' : 'border-white/10 bg-black/30 hover:border-white/25'}`}
            >
              <div className="grid h-16 w-full place-items-center rounded-md bg-white/5">
                <Box aria-hidden="true" className="h-7 w-7 text-white/35" />
              </div>
              <div className="mt-3 min-w-0">
                <p className="truncate text-xs font-medium text-white/90">{modelItem.record.id}</p>
                <p className="mt-1 truncate text-[10px] uppercase text-white/35">
                  {recipe} · {status}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function LibraryThumb({
  item,
  selected,
  onSelect,
}: {
  item: ArtLibraryItem;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const thumbUrl = item.approvedDataUrl ?? item.takes[item.activeTakeIndex]?.dataUrl ?? null;
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`art-library-item-${item.id}`}
      data-selected={selected}
      data-asset-status={item.status}
      className={`flex flex-col gap-1.5 rounded-xl border p-2 text-left transition ${
        selected
          ? 'border-crucible-accent bg-white/10'
          : 'border-white/10 bg-black/30 hover:border-white/25'
      }`}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-white/5">
        {/*
         * `thumbUrl` resolves audio (crucible#2782) AND video (crucible#2669)
         * content-types now, not just images. Branch order: a video-type
         * animation take (`data:video/...`, Kling/Veo mp4) gets a silent
         * looping <video> thumbnail; audio (`data:audio/...`) must fall to the
         * glyph BEFORE the <img> branch or it renders a broken
         * <img src="data:audio/...">. The grid card is a single <button>, so
         * neither audio nor video gets controls here (nesting interactive
         * elements is invalid HTML); the inspector is where a reviewer plays
         * them with controls.
         */}
        {thumbUrl && thumbUrl.startsWith('data:video/') ? (
          <video
            src={thumbUrl}
            className="h-full w-full object-contain"
            autoPlay
            loop
            muted
            playsInline
          />
        ) : item.category !== 'audio' && thumbUrl ? (
          <img
            src={thumbUrl}
            alt={item.id}
            className="h-full w-full object-contain"
            style={{
              backgroundImage:
                'linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wide text-white/40">
            {item.category === 'audio' ? '♪ audio' : 'no preview'}
          </div>
        )}
        {item.status === 'approved' && (
          <span className="absolute right-1 top-1 rounded-full bg-green-500/90 px-1.5 py-0.5 text-[9px] font-semibold text-black">
            ✓
          </span>
        )}
      </div>
      <p className="truncate text-xs font-medium text-white/90">{item.id}</p>
      <p className="truncate text-[10px] uppercase tracking-wide text-white/35">
        {item.category} · {item.status}
      </p>
    </button>
  );
}
